import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import {
	confirmDeclaration,
	getDeclarations,
	resetInheritedKycItems,
	updateDeclarations,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { INVESTOR_BACKUP_PROPERTY } from '@dealstack/frontend/investor/pages/Investment/common/useBackupController';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import {
	DataDeclarationTct,
	DeclarationType,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

export enum SOURCE_OF_FUNDS_TYPE {
	SALARY = 'SOURCE_OF_FUNDS_SALARY',
	INHERITANCE = 'SOURCE_OF_FUNDS_INHERITANCE',
	INVESTMENT_INCOME = 'SOURCE_OF_FUNDS_INVESTMENT_INCOME',
	PERSONAL_SAVINGS = 'SOURCE_OF_FUNDS_PERSONAL_SAVINGS',
	OTHER = 'SOURCE_OF_FUNDS_OTHER',
}

export enum DECLARATIONS_FORM {
	BENEFICIAL_OWNERSHIP = 'BENEFICIAL_OWNERSHIP',
	SOURCE_OF_FUNDS = 'SOURCE_OF_FUNDS',
	SOURCE_OF_FUNDS_OTHER_DETAILS = 'SOURCE_OF_FUNDS_OTHER_DETAILS',
	IS_POLITICALLY_EXPOSED_PERSON = 'POLITICALLY_EXPOSED_PERSON',
	PEP_DETAILS = 'PEP_DETAILS',
}

export const FUNDS_CHECKBOXES = [
	{
		name: SOURCE_OF_FUNDS_TYPE.SALARY,
		label: 'investment.join.kyc.declarations.funds.label_1',
		testId: 'SOURCE_OF_FUNDS_SALARY_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.INHERITANCE,
		label: 'investment.join.kyc.declarations.funds.label_2',
		testId: 'SOURCE_OF_FUNDS_INHERITANCE_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.INVESTMENT_INCOME,
		label: 'investment.join.kyc.declarations.funds.label_3',
		testId: 'SOURCE_OF_FUNDS_INVESTMENT_INCOME_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.PERSONAL_SAVINGS,
		label: 'investment.join.kyc.declarations.funds.label_4',
		testId: 'SOURCE_OF_FUNDS_PERSONAL_SAVINGS_CHECKBOX' as const,
	},
	{
		name: SOURCE_OF_FUNDS_TYPE.OTHER,
		label: 'investment.join.kyc.declarations.funds.label_5',
		testId: 'SOURCE_OF_FUNDS_OTHER_CHECKBOX' as const,
	},
];

export interface DeclarationsFormValues {
	[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: boolean;
	[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: string[];
	[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: string;
	[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: boolean | undefined;
	[DECLARATIONS_FORM.PEP_DETAILS]: string;
}

export const useJoinAndAllocationDeclarationsController = () => {
	const tInvestor = useTranslationInvestor();
	const [isPEPModalVisible, setPEPModalVisible] = useState(false);
	const getDeclarationsRequest = useWrappedRequest(getDeclarations);
	const updateDeclarationsRequest = useWrappedRequest(updateDeclarations);

	const commonYup = useCommonYupValidation();
	const {
		setIsContinueDisabled,
		setIsBackDisabled,
		setIsLoading,
		isPIVInvestment,
		updateBackupData,
		dataBackup,
		removeBackupData,
		investmentDetails,
		kycSetting,
		confirmsDeclarationsBeforeContracts,
	} = useContext(investmentJoinAndAllocationContext);

	const isBeneficialOwnershipRequired = kycSetting?.isBeneficialOwnershipRequired ?? true;
	const isSourceOfFundsRequired = kycSetting?.isSourceOfFundsRequired ?? true;
	const isPepRequired = kycSetting?.isPepRequired ?? true;

	const kycWorkflowId = investmentDetails?.activeKycWorkflowId ?? '';

	const declarationsForm = useForm<DeclarationsFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			Yup.object().shape({
				[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: isBeneficialOwnershipRequired
					? commonYup.requiredConsent
					: Yup.boolean().notRequired(),
				[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: isSourceOfFundsRequired
					? Yup.array()
							.min(1, 'Please check at least one checkbox')
							.required('Please check at least one checkbox')
					: Yup.array().notRequired(),
				[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: Yup.string()
					.when([DECLARATIONS_FORM.SOURCE_OF_FUNDS], {
						is: (value: string[]) =>
							isSourceOfFundsRequired && value?.includes(SOURCE_OF_FUNDS_TYPE.OTHER),
						then: () => commonYup.string.required.nullable(),
						otherwise: () => commonYup.string.optional,
					})
					.nullable(),
				[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: isPepRequired
					? Yup.boolean().required('Please check one checkbox')
					: Yup.boolean().notRequired(),
				[DECLARATIONS_FORM.PEP_DETAILS]: Yup.string()
					.when([DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON], {
						is: (value: boolean) => isPepRequired && value,
						then: () => commonYup.string.required,
					})
					.nullable(),
			})
		),
		defaultValues: {
			[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [],
		},
	});

	const parseResponseToFormValues = (payload: DataDeclarationTct[]) => {
		return payload?.reduce((acc, declaration) => {
			if (declaration.type.includes(DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP)) {
				return {
					...acc,
					[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: declaration.checked,
				};
			}

			if (
				declaration.type.includes(DECLARATIONS_FORM.SOURCE_OF_FUNDS) &&
				declaration.checked
			) {
				if (declaration.type.includes(SOURCE_OF_FUNDS_TYPE.OTHER)) {
					return {
						...acc,
						[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [
							...(acc[DECLARATIONS_FORM.SOURCE_OF_FUNDS] || []),
							declaration.type,
						],
						[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: declaration.description,
					};
				}
				return {
					...acc,
					[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [
						...(acc[DECLARATIONS_FORM.SOURCE_OF_FUNDS] || []),
						declaration.type,
					],
				};
			}

			if (declaration.type.includes(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON)) {
				return {
					...acc,
					[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: declaration.checked,
					[DECLARATIONS_FORM.PEP_DETAILS]: declaration.description,
				};
			}

			return acc;
		}, {} as DeclarationsFormValues);
	};

	useEffect(() => {
		const isPep = declarationsForm.getValues(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON);
		if (isPep) {
			return;
		}

		declarationsForm.clearErrors(DECLARATIONS_FORM.PEP_DETAILS);
	}, [declarationsForm.watch(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON)]);

	useEffect(() => {
		const isFundsOtherChecked = (
			declarationsForm.getValues(DECLARATIONS_FORM.SOURCE_OF_FUNDS) ?? []
		).includes(SOURCE_OF_FUNDS_TYPE.OTHER);
		if (isFundsOtherChecked) {
			return;
		}

		declarationsForm.clearErrors(DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS);
	}, [declarationsForm.watch(DECLARATIONS_FORM.SOURCE_OF_FUNDS)]);

	const getDeclarationsData = () => {
		if (!kycWorkflowId) {
			return;
		}
		getDeclarationsRequest.fetch(kycWorkflowId).onSuccess(({ payload }) => {
			if (!payload) {
				return;
			}
			const formValues = parseResponseToFormValues(payload);
			if (!dataBackup.declarations) {
				declarationsForm.reset(formValues);
			}
		});
	};

	useEffect(() => {
		if (dataBackup.declarations) {
			declarationsForm.reset(dataBackup.declarations);
		}
		getDeclarationsData();
	}, [kycWorkflowId]);

	const isDeclarationsLoaded = !!getDeclarationsRequest.response || !!dataBackup.declarations;
	const isDeclarationsRejected = !!getDeclarationsRequest.response?.payload?.some((declaration) =>
		isKycRejected(declaration.status)
	);

	const SOURCE_OF_FUNDS_TYPES: DeclarationType[] = [
		DeclarationType.SOURCE_OF_FUNDS_SALARY,
		DeclarationType.SOURCE_OF_FUNDS_INHERITANCE,
		DeclarationType.SOURCE_OF_FUNDS_INVESTMENT_INCOME,
		DeclarationType.SOURCE_OF_FUNDS_PERSONAL_SAVINGS,
		DeclarationType.SOURCE_OF_FUNDS_OTHER,
	];
	const declarationsPayload = getDeclarationsRequest.response?.payload ?? [];
	const hasAnySofChecked = declarationsPayload.some(
		(d) => SOURCE_OF_FUNDS_TYPES.includes(d.type) && d.checked
	);
	const isInheritableRequiredDeclaration = (d: {
		type: DeclarationType;
		checked: boolean;
	}): boolean => {
		if (d.type === DeclarationType.BENEFICIAL_OWNERSHIP) {
			return isBeneficialOwnershipRequired;
		}
		if (d.type === DeclarationType.POLITICALLY_EXPOSED_PERSON) {
			return isPepRequired;
		}
		if (SOURCE_OF_FUNDS_TYPES.includes(d.type)) {
			return isSourceOfFundsRequired && hasAnySofChecked;
		}
		return false;
	};
	const hasUnconfirmedDeclarations = declarationsPayload.some(
		(d) =>
			d.subjectAction === SubjectAction.NOT_YET_ACTED && isInheritableRequiredDeclaration(d)
	);
	const hasInheritedDeclarations = declarationsPayload.some(
		(d) => d.subjectAction != null && isInheritableRequiredDeclaration(d)
	);
	const declarationsCreatedAt = getDeclarationsRequest.response?.payload?.[0]?.updatedAt;
	const [inheritedDismissed, setInheritedDismissed] = useState(!!dataBackup.declarations);
	const isInheritedAndLocked = hasUnconfirmedDeclarations && !inheritedDismissed;
	const isConfirmedInherited =
		hasInheritedDeclarations && !hasUnconfirmedDeclarations && !inheritedDismissed;
	const rejectionComment = getDeclarationsRequest.response?.payload?.[0]?.rejectionComment;

	const onConfirmAllDeclarations = async () => {
		const declarations = getDeclarationsRequest.response?.payload;
		if (!declarations) {
			return;
		}
		const unconfirmed = declarations.filter(
			(d) => d.subjectAction === SubjectAction.NOT_YET_ACTED
		);
		await Promise.all(unconfirmed.map((d) => confirmDeclaration(d.id)));
		showToastV2({ title: tInvestor('investment.join.kyc.inherited.confirmed') });
		getDeclarationsData();
	};

	const onDismissInherited = () => {
		setInheritedDismissed(true);
		declarationsForm.reset({
			[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]: false,
			[DECLARATIONS_FORM.SOURCE_OF_FUNDS]: [],
			[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS]: '',
			[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]: undefined,
			[DECLARATIONS_FORM.PEP_DETAILS]: '',
		});
	};

	const onResetInherited = async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.DECLARATION);
		getDeclarationsData();
	};

	useEffect(() => {
		if (isConfirmedInherited) {
			setIsContinueDisabled(false);
			setIsBackDisabled(false);
			return;
		}
		setIsContinueDisabled(
			!!Object.entries(declarationsForm.formState.errors).length ||
				(!declarationsForm.formState.isDirty && isDeclarationsRejected) ||
				!declarationsForm.formState.isValid ||
				isInheritedAndLocked
		);
		setIsBackDisabled(isInheritedAndLocked);
	}, [
		declarationsForm.formState,
		isDeclarationsRejected,
		isInheritedAndLocked,
		isConfirmedInherited,
	]);

	const onSubmit = (): Promise<WizardSaveResult> =>
		new Promise((resolve) => {
			setIsLoading(true);
			if (showConfirmModal) {
				onCloseConfirmationModal();
			}

			const formValues = declarationsForm.getValues();

			/**
			 * D.slaby MIP-776 07.07.2023
			 * When declaration is in fact the same as previous one, don't generate new
			 * Declaration entry
			 */
			if (getDeclarationsRequest.response && !hasUnconfirmedDeclarations) {
				const responseToForm = parseResponseToFormValues(
					getDeclarationsRequest.response.payload ?? []
				);
				const isChanged = JSON.stringify(responseToForm) === JSON.stringify(formValues);
				if (isChanged) {
					showToastV2({
						title: 'Data saved successfully!',
					});
					resolve(SAVE_OK);
					removeBackupData(INVESTOR_BACKUP_PROPERTY.declarations);
					setIsLoading(false);
					return;
				}
			}

			const getIsChecked = (type: SOURCE_OF_FUNDS_TYPE) =>
				(formValues[DECLARATIONS_FORM.SOURCE_OF_FUNDS] ?? []).includes(type);

			updateDeclarationsRequest
				.fetch({
					kycWorkflowId,
					beneficialOwnership: formValues[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP],
					sourceOfFundsSalary: getIsChecked(SOURCE_OF_FUNDS_TYPE.SALARY),
					sourceOfFundsInheritence: getIsChecked(SOURCE_OF_FUNDS_TYPE.INHERITANCE),
					sourceOfFundsInvestmentIncome: getIsChecked(
						SOURCE_OF_FUNDS_TYPE.INVESTMENT_INCOME
					),
					sourceOfFundsPersonalSavings: getIsChecked(
						SOURCE_OF_FUNDS_TYPE.PERSONAL_SAVINGS
					),
					sourceOfFundsOther: getIsChecked(SOURCE_OF_FUNDS_TYPE.OTHER),
					sourceOfFundsOtherDescription:
						formValues[DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS],
					politicallyExposedPerson:
						!!formValues[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON],
					politicallyExposedPersonDescription: formValues[DECLARATIONS_FORM.PEP_DETAILS],
				})
				.onSuccess(() => {
					getDeclarationsData();
					showToastV2({
						title: 'Data saved successfully!',
					});
					removeBackupData(INVESTOR_BACKUP_PROPERTY.declarations);
					resolve(SAVE_OK);
				})
				.onAnyError(() => {
					resolve(SAVE_FAILED);
				})
				.onFinish(() => {
					setIsLoading(false);
				});
		});

	useEffect(() => {
		const showSourceOfFundsTextarea = !!declarationsForm.watch(
			DECLARATIONS_FORM.SOURCE_OF_FUNDS
		)?.[SOURCE_OF_FUNDS_TYPE.OTHER];
		if (!showSourceOfFundsTextarea) {
			declarationsForm.clearErrors(DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS);
		}
	}, [declarationsForm.watch(DECLARATIONS_FORM.SOURCE_OF_FUNDS)?.[SOURCE_OF_FUNDS_TYPE.OTHER]]);

	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [isUserWarned, setIsUserWarned] = useState(false);

	const pendingSaveRef = useRef<((result: WizardSaveResult) => void) | null>(null);
	useEffect(
		() => () => {
			pendingSaveRef.current?.(SAVE_INVALID);
			pendingSaveRef.current = null;
		},
		[]
	);

	const onCloseConfirmationModal = () => {
		setIsUserWarned(true);
		setShowConfirmModal(false);
		pendingSaveRef.current?.(SAVE_INVALID);
		pendingSaveRef.current = null;
	};

	const onConfirmSubmit = async () => {
		const resolvePending = pendingSaveRef.current;
		pendingSaveRef.current = null;
		const result = await onSubmit();
		resolvePending?.(result);
	};

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			if (!isInheritedAndLocked && !isConfirmedInherited) {
				updateBackupData(
					INVESTOR_BACKUP_PROPERTY.declarations,
					declarationsForm.getValues()
				);
			}
			return SAVE_OK;
		}
		const isValid = await declarationsForm.trigger();
		if (!isValid) {
			return SAVE_INVALID;
		}
		const isDirty = !isEmpty(declarationsForm.formState.dirtyFields);
		if (!isDirty && !dataBackup.declarations) {
			return SAVE_OK;
		}
		if (!confirmsDeclarationsBeforeContracts || isUserWarned) {
			return onSubmit();
		}
		return new Promise<WizardSaveResult>((resolve) => {
			pendingSaveRef.current = resolve;
			setShowConfirmModal(true);
		});
	});

	return {
		isPEPModalVisible,
		setPEPModalVisible,
		declarationsForm,
		onSubmit,
		isPIVInvestment,
		isDeclarationsRejected,
		rejectionComment,
		showConfirmModal,
		onCloseConfirmationModal,
		onConfirmSubmit,
		isDeclarationsLoaded,
		isInheritedAndLocked,
		onConfirmAllDeclarations,
		onDismissInherited,
		onResetInherited,
		declarationsCreatedAt,
		hasInheritedDeclarations,
		isConfirmedInherited,
		isBeneficialOwnershipRequired,
		isSourceOfFundsRequired,
		isPepRequired,
	};
};
